import { AppError } from "@/lib/utils/errors";
import { conversationService } from "@/modules/services/conversation/conversationService";
import { conversationFileService } from "@/modules/services/storage/conversationFile";
import { useConversationStore } from "@/modules/store/conversationStore";
import {
  Attachment,
  Message,
  MessageCreate,
} from "@/modules/types/conversation/message";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import FileResizer from "react-image-file-resizer";

export const useMessageOfConversation = (conversationId: string) => {
  const {
    conversations,
    getMessagesStart,
    getMessagesSuccess,
    getMessagesFailure,
    setLoading,
  } = useConversationStore();
  const limit = 20;

  // Get data from the Zustand store
  const conversationData = conversations[conversationId];

  const messages = conversationData?.messages || [];
  const loading = conversationData?.loading || false;
  // Fix: Use nullish coalescing to only default to true when hasMore is undefined
  const hasMore = conversationData?.hasMore ?? true; // Default to true only when undefined
  const error = conversationData?.error || null;
  const nextId = conversationData?.nextId || undefined;
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [dataLength, setDataLength] = useState(messages.length);

  const fetchMessages = useCallback(async () => {
    if (!conversationId || !hasMore || loading) return; // Prevent multiple fetches
    getMessagesStart(conversationId);
    if (firstLoad) {
      setFirstLoad(false);
    }
    try {
      const resp = await conversationService.getMessageByConversationId(
        conversationId,
        limit,
        nextId
      );

      if (resp) {
        const totalCurrentMessages = dataLength + resp.messages.length;
        setDataLength(totalCurrentMessages);
        getMessagesSuccess({
          conversationId: conversationId,
          messages: resp.messages,
          hasMore: totalCurrentMessages < resp.total, // Changed from <= to <
          nextId: resp.nextId,
        });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      getMessagesFailure({
        conversationId: conversationId,
        error: appErr.message,
      });
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setLoading({ conversationId, loading: false });
    }
  }, [
    conversationId,
    limit,
    nextId,
    hasMore,
    loading,
    dataLength,
    firstLoad,
    getMessagesStart,
    getMessagesSuccess,
    getMessagesFailure,
    setLoading,
  ]);

  const getMessages = async () => {
    await fetchMessages();
  };

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  }, [error]);

  useEffect(() => {
    if (conversationId && conversationData && messages.length <= 0) {
      fetchMessages();
    }
  }, [conversationId, conversationData, messages.length, fetchMessages]);

  return {
    limit,
    messages,
    loading,
    hasMore,
    dataLength,
    getMessages,
  };
};

export const useInputMessage = (
  conversationId: string,
  userId: string,
  userName: string,
  onMessageSent?: (message: Message) => void
) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resizeFile = (fileToResize: File) =>
    new Promise<File>((resolve) => {
      const cropPercentage = fileToResize.size > 900000 ? 30 : 70;
      FileResizer.imageFileResizer(
        fileToResize,
        500,
        500,
        "JPEG",
        cropPercentage,
        0,
        (file) => {
          const fileData = file as File;
          if (fileData) {
            resolve(fileData);
          }
        },
        "file"
      );
    });

  const sendMessage = useCallback(
    async (content: string, files?: File[], replyToMessageId?: string) => {
      if (!content.trim() && (!files || files.length === 0)) {
        setError("Message cannot be empty");
        return;
      }

      setSending(true);
      setError(null);

      try {
        // First, upload attachments if any
        let attachments: Attachment[] = [];

        if (files && files.length > 0) {
          // This would be where you'd upload files and get URLs back
          // For example:
          // attachments = await uploadFiles(files);

          // Placeholder implementation - replace with actual file upload
          attachments = await Promise.all(
            Array.from(files).map(async (file) => {
              // In a real implementation, you would upload the file and get back a URL
              // This is a mock implementation
              let fileData = file;
              if (file.type.includes("image")) {
                fileData = await resizeFile(file);
              }
              const randomId = Math.random().toString(36).substring(2, 15);
              const fileName = `${randomId}_${file.name}`;
              const url = await conversationFileService.uploadFile(
                fileData,
                conversationId,
                fileName
              );
              return {
                type: file.type.startsWith("image/") ? "image" : "file",
                url: url, // This is temporary and won't work in production
                fileName: fileName,
                fileSize: file.size,
                contentType: file.type,
                messageId: "", // This will be filled in by the server
              };
            })
          );
        }

        // Create the message object
        const messageData: MessageCreate = {
          conversationId,
          senderId: userId,
          senderName: userName,
          content,
          attachments: attachments.length > 0 ? attachments : undefined,
          replyTo: replyToMessageId,
        };

        // Send the message
        const response = await conversationService.sendMessage(messageData);

        if (response.messageData) {
          // Call the callback with the new message
          onMessageSent?.(response.messageData);
          return response.messageData;
        } else {
          throw new Error("Failed to send message: No message data returned");
        }
      } catch (err) {
        const appErr = new AppError(err);
        appErr.logError();
        const errorMessage = appErr.message || "Failed to send message";
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setSending(false);
      }
    },
    [conversationId, userId, userName, onMessageSent]
  );

  return {
    sendMessage,
    sending,
    error,
  };
};

export const useMessage = () => {
  const {
    conversations,
    addMessage,
    getMessagesStart,
    getMessagesSuccess,
    getMessagesFailure,
    setLoading,
  } = useConversationStore();

  const fetchMessages = async (
    conversationId: string,
    hasMore: boolean,
    loading: boolean,
    limit: number,
    nextId?: string
  ) => {
    if (!conversationId || !hasMore || loading) return; // Prevent multiple fetches
    getMessagesStart(conversationId);
    try {
      const resp = await conversationService.getMessageByConversationId(
        conversationId,
        limit,
        nextId
      );

      if (resp) {
        getMessagesSuccess({
          conversationId: conversationId,
          messages: resp.messages,
          hasMore: resp.nextId !== null,
          nextId: resp.nextId,
        });
      }
    } catch (err) {
      const appErr = new AppError(err);
      appErr.logError();
      getMessagesFailure({
        conversationId: conversationId,
        error: appErr.message,
      });
      enqueueSnackbar(appErr.message, { variant: "error" });
    } finally {
      setLoading({ conversationId, loading: false });
    }
  };

  const appendMessage = (conversationId: string, message: Message) => {
    if (
      conversations[conversationId] &&
      conversations[conversationId].messages.length > 0
    ) {
      addMessage({ conversationId, message });
    } else {
      fetchMessages(conversationId, true, false, 10);
    }
  };

  return {
    appendMessage,
  };
};
